import React, { useEffect, useState } from "react";
import { Menu, X, HardHat, LayoutDashboard, LogOut, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { NAV_LINKS } from "../mock";
import { useAuth } from "../context/AuthContext";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-[70px]">
          <a href="#top" className="flex items-center gap-2.5 group">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#1D4ED8] text-white group-hover:bg-[#DC2626] transition-colors duration-300">
              <HardHat className="h-5 w-5" />
            </span>
            <span className="font-display text-xl font-700 tracking-tight text-[#0A2540]">
              cost<span className="text-[#DC2626]">2</span>build<span className="text-[#1D4ED8]">.ai</span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <button key={l.href} onClick={() => scrollTo(l.href)} className="text-sm font-medium text-slate-600 hover:text-[#1D4ED8] transition-colors">
                {l.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <>
                <span className="flex items-center gap-1.5 rounded-full bg-blue-50 text-[#1D4ED8] text-sm font-semibold px-3 py-1.5" title="Render points">
                  <Zap className="h-3.5 w-3.5" /> {user.points ?? 0} pts
                </span>
                <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-700 hover:text-[#1D4ED8] hover:bg-blue-50">
                  <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                </Button>
                <div className="flex items-center gap-2 pl-1">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full ring-2 ring-blue-100" referrerPolicy="no-referrer" />
                  ) : (
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1D4ED8] text-white text-sm font-semibold">
                      {user.name?.charAt(0) || "U"}
                    </span>
                  )}
                  <button onClick={handleLogout} className="text-slate-500 hover:text-[#DC2626] transition-colors" title="Log out">
                    <LogOut className="h-4 w-4" />
                  </button>
                </div>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={login} className="text-slate-700 hover:text-[#1D4ED8] hover:bg-blue-50">
                  Sign in
                </Button>
                <Button onClick={() => scrollTo("#estimator")} className="bg-[#DC2626] hover:bg-[#b91c1c] text-white shadow-sm">
                  Start estimating
                </Button>
              </>
            )}
          </div>

          <button className="lg:hidden text-[#0A2540]" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-b border-slate-200 px-5 py-4 space-y-1">
          {NAV_LINKS.map((l) => (
            <button key={l.href} onClick={() => scrollTo(l.href)} className="block w-full text-left py-2.5 text-slate-700 font-medium">
              {l.label}
            </button>
          ))}
          <div className="pt-3 flex flex-col gap-2">
            {user ? (
              <>
                <Button variant="outline" onClick={() => navigate("/dashboard")} className="w-full border-[#1D4ED8] text-[#1D4ED8]">Dashboard</Button>
                <Button onClick={handleLogout} className="w-full bg-[#DC2626] text-white">Log out</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={login} className="w-full border-[#1D4ED8] text-[#1D4ED8]">Sign in</Button>
                <Button onClick={() => scrollTo("#estimator")} className="w-full bg-[#DC2626] text-white">Start estimating</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
